import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchAssortmentPlans,
  deletePlan,
  copyPlan,
  CopyPlanParams,
  updatePlanName,
  updatePlanHeader,
} from '@/api/assortmentPlans'
import { queryKeys } from '@/lib/queryKeys'
import type {
  ApiPlanDetailResponse,
  AssortmentPlansFilters,
  AssortmentPlansResponse,
  PlanDetailResponse,
} from '@/types'
import { toast } from '@/stores/useToastStore'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'
import { buildHierarchyBreadcrumb } from '@/utils/formatters'

function invalidateDagQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  planId: string
) {
  const versionId =
    useCurrentVersionStore.getState().versionsByPlan[planId] ??
    queryClient.getQueryData<string>(queryKeys.planActiveVersion.byId(planId))

  if (!versionId) return

  queryClient.invalidateQueries({
    queryKey: queryKeys.dagTable.metadata(planId, versionId),
  })
  queryClient.invalidateQueries({
    queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
  })
  queryClient.invalidateQueries({
    queryKey: ['store-forecast', planId, versionId],
  })
}

export function useAssortmentPlans(filters: AssortmentPlansFilters = {}) {
  const {
    search,
    status,
    owner_id,
    hierarchy_id,
    season_id,
    page = 1,
    pageSize = 20,
  } = filters

  return useQuery<AssortmentPlansResponse>({
    queryKey: queryKeys.assortmentPlans.list({
      search,
      status,
      owner_id,
      hierarchy_id,
      season_id,
      page,
      pageSize,
    }),
    queryFn: () => fetchAssortmentPlans(filters),
    refetchInterval: 30000, // Refresh every 30 seconds
  })
}

export function useDeletePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deletePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assortmentPlans.all })
      toast.success('Plan deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete plan')
    },
  })
}

export function useCopyPlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CopyPlanParams) => copyPlan(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assortmentPlans.all })
      toast.success('Plan copied successfully')
    },
    onError: () => {
      toast.error('Failed to copy plan')
    },
  })
}

export function useUpdatePlanName() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ planId, name }: { planId: string; name: string }) =>
      updatePlanName(planId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assortmentPlans.all })
      toast.success('Plan name updated')
    },
    onError: () => {
      toast.error('Failed to update plan name')
    },
  })
}

export function useUpdatePlanHeader(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: Record<string, string | number | string[] | null>) =>
      updatePlanHeader(planId, updates),
    onSuccess: (data: ApiPlanDetailResponse) => {
      queryClient.setQueryData(queryKeys.planDetail.metadata(planId), data)
      queryClient.setQueryData(
        queryKeys.planActiveVersion.byId(planId),
        data.active_version_id
      )
      queryClient.setQueriesData<PlanDetailResponse>(
        { queryKey: queryKeys.planDetail.byId(planId) },
        existing => {
          if (!existing) return existing

          return {
            ...existing,
            name: data.name,
            status: data.status,
            hierarchy: buildHierarchyBreadcrumb(data.hierarchy),
          }
        }
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.assortmentPlans.all,
      })
      invalidateDagQueries(queryClient, planId)
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to update plan'
      toast.error(message)
    },
  })
}
